import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, X, Search } from 'lucide-react';
import { API } from '../lib/api';

const fmtCnpj = (d) => { const s = String(d || '').replace(/\D/g, ''); return s.length === 14 ? s.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : s; };

const ERP = `${API}/erp`;

export const syncToast = (data) => {
  const parts = [];
  if (data.received) {
    const bs = data.by_schema || {};
    const det = Object.entries(bs).map(([k, v]) => `${v} ${k}`).join(', ');
    parts.push(`${data.received} documento(s) recebido(s)${det ? ` (${det})` : ''}`);
  }
  if (data.skipped_old) parts.push(`${data.skipped_old} fora da janela de historico`);
  if (data.cstat === '138' || data.inserted_or_updated > 0) {
    toast.success(`Sync OK: ${data.inserted_or_updated} nota(s) gravada(s). uLtNSU ${data.ult_nsu}${parts.length ? ` — ${parts.join('; ')}` : ''}`, { duration: 9000 });
  } else if (data.cstat === '137') {
    toast.info(`Nenhum DF-e novo na SEFAZ (uLtNSU ${data.ult_nsu} = maxNSU ${data.max_nsu}). Se uma nota existe e nao aparece, use "Buscar pela chave".`, { duration: 9000 });
  } else if (data.cstat === '656') {
    toast.info('SEFAZ pediu para aguardar 1 hora (656). Nao ha DF-e novo — cadastro e certificado estao OK.', { duration: 9000 });
  } else {
    toast.error(`SEFAZ ${data.cstat}: ${(data.xmotivo || '').slice(0, 150)}`, { duration: 8000 });
  }
};

export const runManifestoSync = async (companyId, { reset = false } = {}) => {
  const tid = toast.loading(reset ? 'Rebuscando historico completo na SEFAZ (NSU 0, pode demorar)...' : 'Consultando SEFAZ DistribuicaoDFe...');
  try {
    const params = new URLSearchParams();
    if (reset) params.set('force_nsu', '0');
    if (companyId) params.set('company_id', companyId);
    const { data } = await axios.post(`${ERP}/manifesto/sync?${params}`, {}, { withCredentials: true, timeout: 180000 });
    toast.dismiss(tid);
    syncToast(data);
    return data;
  } catch (err) {
    toast.dismiss(tid);
    toast.error(err.response?.data?.detail || 'Erro no sync', { duration: 8000 });
    return null;
  }
};

export const ManifestoFetchChaveModal = ({ companyId, onClose, onDone }) => {
  const [chave, setChave] = useState('');
  const [ciencia, setCiencia] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const digits = chave.replace(/\D/g, '');
  const submit = async () => {
    if (digits.length !== 44) { toast.error('A chave de acesso deve ter 44 digitos'); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${ERP}/manifesto/fetch-chave`, { chave: digits, company_id: companyId || null, ciencia }, { withCredentials: true, timeout: 120000 });
      setResult(data);
      if (data.doc) {
        if (data.too_old) toast.info(data.hint || 'Chave antiga (>180 dias): registrada no Manifesto para referencia em devolucao.', { duration: 12000 });
        else if (data.awaiting_full_xml) toast.info('Nota registrada no Manifesto — aguardando a SEFAZ liberar o XML completo (vem na proxima sincronizacao).', { duration: 9000 });
        else toast.success(`Nota ${data.doc.emitente_nome || ''} ${data.doc.has_full_xml ? '(XML completo)' : '(resumo)'} gravada no Manifesto`);
        onDone && onDone(data.doc);
      } else {
        toast.error(`SEFAZ ${data.cstat}: ${(data.xmotivo || '').slice(0, 160)}`, { duration: 9000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao consultar a SEFAZ', { duration: 8000 });
    } finally { setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={e => e.stopPropagation()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" data-testid="manifesto-fetch-chave-modal">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-black text-slate-900 flex items-center gap-2"><KeyRound size={18} className="text-violet-600" /> Buscar nota pela chave de acesso</h3>
            <p className="text-xs text-slate-500 mt-1">Consulta a SEFAZ diretamente pela chave (44 digitos) da nota emitida contra o CNPJ desta filial. Use quando a nota existe (DANFE em maos) mas nao apareceu na sincronizacao.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="manifesto-fetch-chave-close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <input value={chave} onChange={e => setChave(e.target.value)} placeholder="Chave de acesso (44 digitos)" className="w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm font-mono text-slate-900 focus:outline-none focus:border-violet-600 focus:ring-2 focus:ring-violet-500/20" data-testid="manifesto-fetch-chave-input" />
          <p className={`text-[11px] ${digits.length === 44 ? 'text-emerald-600' : 'text-slate-400'}`}>{digits.length}/44 digitos</p>
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={ciencia} onChange={e => setCiencia(e.target.checked)} className="mt-0.5 accent-violet-600" data-testid="manifesto-fetch-chave-ciencia" />
            <span>Registrar <b>Ciencia da Operacao</b> antes de baixar (recomendado — libera o XML completo para dar entrada na compra)</span>
          </label>
          {result && !result.doc && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 space-y-1.5" data-testid="manifesto-fetch-chave-error">
              <p><b>SEFAZ {result.cstat}</b>: {result.xmotivo}</p>
              {result.evento && <p className="text-slate-600">Evento Ciencia: <b>{result.evento.cstat}</b> {result.evento.xmotivo}{result.evento.lote_cstat && result.evento.lote_cstat !== result.evento.cstat ? ` (lote ${result.evento.lote_cstat})` : ''}</p>}
              {result.situacao && (
                <p className="text-slate-600">Situacao na SEFAZ-{result.situacao.uf_emitente || '?'} (emitente): {result.situacao.cstat ? <><b>{result.situacao.cstat}</b> {result.situacao.xmotivo}{result.situacao.nprot ? ` · prot. ${result.situacao.nprot}` : ''}</> : (result.situacao.erro || 'indisponivel')}</p>
              )}
              {result.diag && (
                <div className="pt-1.5 mt-1 border-t border-rose-200 text-slate-700">
                  <p className="font-bold text-slate-800">Verifique no DANFE:</p>
                  <p>Destinatario deve ser o CNPJ <b className="font-mono">{fmtCnpj(result.diag.cnpj_consultado)}</b> (CNPJ da filial consultada){result.diag.cert_cnpj && result.diag.cert_cnpj !== result.diag.cnpj_consultado ? <> — <span className="text-amber-700 font-bold">certificado e de outro CNPJ ({fmtCnpj(result.diag.cert_cnpj)})</span></> : ''}.</p>
                  <p>Ambiente: <b className={result.diag.ambiente === 'producao' ? 'text-emerald-700' : 'text-amber-700'}>{result.diag.ambiente === 'producao' ? 'Producao' : 'Homologacao (notas reais NAO aparecem!)'}</b> · UF autor: {result.diag.uf_autor} · Emitente (da chave): <span className="font-mono">{fmtCnpj(result.diag.chave_cnpj_emitente)}</span></p>
                  {result.cstat === '137' && <p className="mt-1 text-slate-600">A SEFAZ nao tem essa nota distribuida para este CNPJ. Se o destinatario no DANFE for outro CNPJ (matriz x filial), selecione a filial correta no topo e tente de novo.</p>}
                </div>
              )}
            </div>
          )}
          {result?.doc && result.awaiting_full_xml && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-1.5" data-testid="manifesto-fetch-chave-awaiting">
              <p className="font-bold">Nota confirmada — aguardando a SEFAZ liberar o XML</p>
              {result.situacao?.cstat === '100' && <p>Situacao na SEFAZ-{result.situacao.uf_emitente}: <b>100 Autorizado</b>{result.situacao.nprot ? ` · prot. ${result.situacao.nprot}` : ''}.</p>}
              {result.evento && <p>Ciencia da Operacao: <b>{result.evento.cstat}</b> {result.evento.cstat === '573' ? 'ja registrada anteriormente' : result.evento.xmotivo}.</p>}
              <p>A nota foi registrada no Manifesto como <b>&quot;Aguardando XML&quot;</b>. O Ambiente Nacional gera o XML completo apos a Ciencia (minutos a algumas horas) e ele chega sozinho na proxima <b>Sincronizar SEFAZ</b>{result.wait_min > 0 ? ` (liberada em ~${result.wait_min} min — a SEFAZ exige 1 h de espera apos retornar 137/656)` : ''}.</p>
              {result.skipped_window && <p className="text-amber-700">Consulta por chave nao enviada agora para evitar novo bloqueio (656).</p>}
            </div>
          )}
          {result?.doc && !result.awaiting_full_xml && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="manifesto-fetch-chave-success">
              <p className="font-bold">{result.doc.emitente_nome} · R$ {Number(result.doc.valor_nf || 0).toFixed(2).replace('.', ',')}</p>
              <p className="mt-0.5">{result.doc.has_full_xml ? 'XML completo recebido — pronto para dar entrada.' : 'Resumo recebido — registre a Ciencia para liberar o XML completo.'}</p>
              {result.evento && <p className="mt-1 text-slate-600">Evento Ciencia: {result.evento.cstat} {result.evento.xmotivo}</p>}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100" data-testid="manifesto-fetch-chave-cancel">Fechar</button>
          <button onClick={submit} disabled={busy || digits.length !== 44} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white text-sm font-bold flex items-center gap-2" data-testid="manifesto-fetch-chave-submit">
            {busy ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Search size={14} />} Consultar SEFAZ
          </button>
        </div>
      </div>
    </div>, document.body);
};
